import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {IconTrash} from "@tabler/icons-react";
import React, {useEffect, useState} from "react";

const emptyForm = {name: "", description: "", content: ""};

export const CreateSkillModal = ({open, skill, handleClose, handleSubmit, onRemove}) => {
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    const isEdit = !!skill?.id;

    useEffect(() => {
        if (open) {
            setForm(
                skill
                    ? {
                        name: skill.name || "",
                        description: skill.description || "",
                        content: skill.content || "",
                    }
                    : emptyForm
            );
            setErrors({});
        }
    }, [open, skill]);

    const setField = (key) => (e) => {
        setForm((prev) => ({...prev, [key]: e.target.value}));
        setErrors((prev) => ({...prev, [key]: undefined}));
    };

    const validate = () => {
        const next = {};
        if (!form.name.trim()) next.name = "Name is required";
        if (!form.content.trim()) next.content = "Content is required";
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const onSave = async () => {
        if (!validate()) return;
        try {
            setSaving(true);
            const payload = {
                id: skill?.id || crypto.randomUUID(),
                name: form.name.trim(),
                description: form.description.trim(),
                content: form.content,
            };
            await handleSubmit(payload);
            handleClose();
        } catch (e) {
            console.error("Failed to save skill:", e);
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async () => {
        if (!skill?.id) return;
        try {
            setSaving(true);
            await onRemove(skill.id);
            handleClose();
        } catch (e) {
            console.error("Failed to delete skill:", e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontSize: 16, fontWeight: 600}}>
                {isEdit ? "Edit Skill" : "New Skill"}
            </DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{mt: 1}}>
                    <TextField
                        label="Name"
                        size="small"
                        fullWidth
                        value={form.name}
                        onChange={setField("name")}
                        error={!!errors.name}
                        helperText={errors.name}
                        InputProps={{sx: {borderRadius: 0}}}
                    />
                    <TextField
                        label="Description"
                        size="small"
                        fullWidth
                        value={form.description}
                        onChange={setField("description")}
                        placeholder="When should this skill be used?"
                        InputProps={{sx: {borderRadius: 0}}}
                    />
                    <Box>
                        <TextField
                            label="Content"
                            size="small"
                            fullWidth
                            multiline
                            minRows={6}
                            value={form.content}
                            onChange={setField("content")}
                            error={!!errors.content}
                            helperText={errors.content}
                            placeholder="The prompt text / instructions..."
                            InputProps={{sx: {borderRadius: 0, fontFamily: "monospace", fontSize: 13}}}
                        />
                        <Typography variant="caption" color="text.secondary">
                            This is the reusable prompt that gets injected when the skill is used.
                        </Typography>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2, justifyContent: "space-between"}}>
                <Box>
                    {isEdit && (
                        <IconButton color="error" onClick={onDelete} disabled={saving}>
                            <IconTrash size={18}/>
                        </IconButton>
                    )}
                </Box>
                <Stack direction="row" gap={1}>
                    <Button onClick={handleClose} disabled={saving} sx={{textTransform: "none"}}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={onSave}
                        disabled={saving}
                        sx={{textTransform: "none", borderRadius: 0}}
                    >
                        {isEdit ? "Save" : "Create"}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default CreateSkillModal;